/* Match HUB — vertical ORIO-style cards in a horizontal swipeable carousel
   with peek of next/previous card. Each card has a hero image at the top
   (ORIO illustration). 3 modes: Walking · Park Room · Educatori (coming soon).
*/
import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Footprints, MapPin, GraduationCap, Lock, Sparkles, ArrowRight,
  PawPrint, ChevronRight, Video,
} from "lucide-react";

import MatchWalking from "./match/MatchWalking";
import ParkRoom from "./match/ParkRoom";
import Stories from "./match/Stories";
import MapGreetings from "./match/MapGreetings";
import { hapticTap } from "@/lib/haptics";
import { orioIllustration, orioIllustrationFallback } from "@/lib/illustrations";

export default function Match() {
  const [params, setParams] = useSearchParams();
  // Deep-links:
  //   ?tab=stories       → open Stories mode
  //   ?openChat=<mid>    → open Walking mode, then MatchWalking auto-opens the ChatSheet
  const initialMode = params.get("tab") === "stories"
    ? "stories"
    : (params.get("openChat") ? "walking" : "hub");
  const [mode, setMode] = useState(initialMode);

  // Sync URL ?tab= with local mode (deep link support from Home banner).
  useEffect(() => {
    if (params.get("tab") === "stories" && mode !== "stories") setMode("stories");
    if (params.get("openChat") && mode === "hub") setMode("walking");
  }, [params, mode]);

  const goHub = () => {
    setMode("hub");
    if (params.get("tab") || params.get("openChat")) {
      params.delete("tab");
      params.delete("openChat");
      setParams(params, { replace: true });
    }
  };

  if (mode === "walking") return <MatchWalking onBack={goHub} />;
  if (mode === "park")    return <ParkRoom onBack={goHub} />;
  if (mode === "stories") return <Stories onBack={goHub} />;

  return <MatchHub onPick={setMode} />;
}

const SLIDES = [
  {
    id: "walking",
    photo: "match-walking-v2",
    eyebrow: "1 v 1",
    eyebrowColor: "#C25612",
    title: "In Passeggiata",
    body: "Tinder a 4 zampe. Swipe chi è in giro adesso col cane.",
    cta: "Inizia a fare swipe",
    ctaIcon: Footprints,
    gradient: "linear-gradient(135deg, #FFF1DF 0%, #FFE5C2 50%, #FFD9A6 100%)",
    accent: "#C25612",
    badge: "Swipe · Super Like · Chat 10 msg",
    disabled: false,
  },
  {
    id: "park",
    photo: "match-park-v2",
    eyebrow: "Open Session",
    eyebrowColor: "#1E7E3F",
    title: "Park Room",
    body: "Scegli un parco sulla mappa o crea un punto d'incontro. Stanza aperta 3h, chat di gruppo, push agli altri padroni.",
    cta: "Apri la mappa",
    ctaIcon: MapPin,
    gradient: "linear-gradient(135deg, #E8F8EE 0%, #D6F0DF 50%, #BCE5C9 100%)",
    accent: "#1E7E3F",
    badge: "Mappa · Chat gruppo · 3h",
    disabled: false,
  },
  {
    id: "stories",
    photo: "match-stories-v2",
    eyebrow: "Reels · 12h",
    eyebrowColor: "#FF7A1A",
    title: "Storie ORIO",
    body: "Reels a 4 zampe. Guarda cosa fanno i cani in giro adesso, reagisci con un ❤️ o un 🐾 e trasforma le visualizzazioni in match.",
    cta: "Guarda le storie",
    ctaIcon: Video,
    gradient: "linear-gradient(135deg, #FFF1DF 0%, #FFE8CB 50%, #F7CE9E 100%)",
    accent: "#FF7A1A",
    badge: "Nazionale · 25km · +Karma",
    disabled: false,
  },
  {
    id: "trainers",
    photo: "match-educator-v2",
    eyebrow: "Anteprima",
    eyebrowColor: "#7B61FF",
    title: "Educatori Cinofili",
    body: "Matching con educatori, comportamentalisti e toelettatori verificati. Prenoti la sessione, paghi nell'app.",
    cta: "Anteprima",
    ctaIcon: GraduationCap,
    gradient: "linear-gradient(135deg, #F1ECFF 0%, #E5DCFB 50%, #D2C2F4 100%)",
    accent: "#7B61FF",
    badge: "Verificati · Prenotazione · Pagamento",
    disabled: true,
  },
];

function MatchHub({ onPick }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const scrollerRef = useRef(null);

  // Track which card is in view to render the dot indicator
  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardWidth = el.scrollWidth / SLIDES.length;
    const idx = Math.round(el.scrollLeft / cardWidth);
    if (idx !== activeIdx) setActiveIdx(idx);
  };

  const goTo = (i) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardWidth = el.scrollWidth / SLIDES.length;
    el.scrollTo({ left: i * cardWidth, behavior: "smooth" });
  };

  return (
    <div className="relative min-h-[100dvh] overflow-x-hidden">
      <HubAurora />

      <div className="relative z-10 max-w-2xl mx-auto pt-6 pb-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="px-4 sm:px-6"
        >
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#9A9A9A] inline-flex items-center gap-1.5">
            <PawPrint className="h-3 w-3" /> Match a 4 zampe
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] mt-1">
            Scegli <span className="orio-grad-text">come</span> conoscerti.
          </h1>
          <p className="mt-2 text-[13.5px] text-[#6B6B6B] max-w-lg leading-relaxed">
            Tre modi diversi per trovare compagni di passeggiata. Scorri per esplorare.
          </p>

          {/* Saluti dalla mappa — appare solo se ci sono saluti ricevuti */}
          <MapGreetings />
        </motion.div>

        {/* Vertical cards — horizontal swipeable carousel with peek */}
        <motion.div
          variants={{ visible: { transition: { staggerChildren: 0.10, delayChildren: 0.18 } } }}
          initial="hidden" animate="visible"
          className="mt-7"
        >
          <div
            ref={scrollerRef}
            onScroll={onScroll}
            className="flex gap-4 overflow-x-auto orio-h-scroll snap-x snap-mandatory pb-3 px-4 sm:px-6"
            style={{
              scrollPaddingInline: "16px",
              scrollSnapType: "x mandatory",
              WebkitOverflowScrolling: "touch",
            }}
            data-testid="match-hub-carousel"
          >
            {SLIDES.map((s, i) => (
              <ModeCardVertical
                key={s.id}
                slide={s}
                index={i}
                onPick={() => {
                  if (s.disabled) return;
                  hapticTap();
                  onPick(s.id);
                }}
              />
            ))}
          </div>

          {/* Dot indicators */}
          <div className="mt-4 flex items-center justify-center gap-2" data-testid="match-hub-dots">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Vai alla scheda ${i + 1}`}
                data-testid={`match-dot-${i}`}
                className="transition-all"
                style={{
                  width: i === activeIdx ? 28 : 8,
                  height: 8,
                  borderRadius: 999,
                  background: i === activeIdx ? SLIDES[activeIdx].accent : "#E5DECF",
                }}
              />
            ))}
          </div>
        </motion.div>

        {/* Footer hint */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
          className="mt-7 text-center text-[11px] text-[#9A9A9A] px-6"
        >
          <Sparkles className="inline h-3 w-3 mr-1" /> Scorri orizzontalmente · Tocca per entrare
        </motion.div>
      </div>
    </div>
  );
}

function ModeCardVertical({ slide, index, onPick }) {
  const { photo, eyebrow, eyebrowColor, title, body, cta, ctaIcon: CtaIcon, gradient, accent, badge, disabled } = slide;

  return (
    <motion.button
      variants={{ hidden: { opacity: 0, y: 28 }, visible: { opacity: 1, y: 0 } }}
      whileHover={!disabled ? { y: -4, scale: 1.005 } : {}}
      whileTap={!disabled ? { scale: 0.985 } : {}}
      transition={{ type: "spring", damping: 24, stiffness: 280 }}
      onClick={onPick}
      data-testid={`mode-${slide.id}`}
      aria-disabled={disabled}
      className="relative shrink-0 snap-start text-left rounded-[32px] overflow-hidden border border-[#F0EBE3] bg-white"
      style={{
        width: "min(84vw, 360px)",
        minHeight: 480,
        boxShadow: "0 22px 48px -22px rgba(0,0,0,0.22), 0 2px 6px -2px rgba(0,0,0,0.04)",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {/* Hero image — bigger top area (Trade Republic style) */}
      <div className="relative h-[260px] overflow-hidden">
        <img
          src={orioIllustration(photo)}
          onError={(e) => { e.currentTarget.src = orioIllustrationFallback(photo); }}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: disabled ? "saturate(0.55)" : "saturate(1.06)" }}
          loading="lazy"
        />
        {/* Subtle bottom gradient for depth */}
        <div aria-hidden className="absolute inset-x-0 bottom-0 h-24"
          style={{ background: "linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.85) 100%)" }} />

        {/* Eyebrow badge top-left */}
        <span
          className="absolute top-4 left-4 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] bg-white/95 backdrop-blur"
          style={{ color: eyebrowColor, boxShadow: "0 4px 12px -4px rgba(0,0,0,0.15)" }}
        >
          {eyebrow}
        </span>

        {/* Coming soon overlay */}
        {disabled && (
          <>
            <div aria-hidden className="absolute inset-0 backdrop-blur-[1.5px]" style={{ background: "rgba(255,255,255,0.20)" }} />
            <motion.div
              aria-hidden
              animate={{ x: ["-50%", "150%"] }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              className="absolute -top-1 left-0 h-full w-1/3 opacity-[0.28]"
              style={{ background: "linear-gradient(120deg, transparent, rgba(255,255,255,0.7), transparent)", filter: "blur(8px)" }}
            />
            <motion.span
              initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ delay: 0.5 + index * 0.1, type: "spring", damping: 12 }}
              className="absolute top-4 right-4 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] inline-flex items-center gap-1.5 text-white"
              style={{
                background: "linear-gradient(135deg, #7B61FF 0%, #FF7A1A 100%)",
                boxShadow: "0 8px 20px -6px rgba(123,97,255,0.55)",
              }}
            >
              <Lock className="h-3 w-3" /> In arrivo
            </motion.span>
          </>
        )}
      </div>

      {/* Content — Trade Republic minimal: title + body + feature chip + arrow CTA */}
      <div className="relative px-6 pt-6 pb-6 flex flex-col">
        <h3 className="font-display font-bold text-3xl text-[#0F0F0F] tracking-tight leading-[1.05]"
          style={{ filter: disabled ? "saturate(0.85)" : "none" }}>
          {title}
        </h3>
        <p className="mt-2.5 text-[13.5px] text-[#6B6B6B] leading-relaxed line-clamp-3">{body}</p>

        {/* Feature chip — TR style */}
        <div className="mt-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-semibold self-start"
          style={{
            background: disabled ? "#F4EFE6" : `${accent}12`,
            color: disabled ? "#9A9A9A" : accent,
            border: `1px solid ${disabled ? "#F0EBE3" : `${accent}33`}`,
          }}>
          <ChevronRight className="h-3 w-3" /> {badge}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <span className="text-[13px] font-semibold" style={{ color: disabled ? "#9A9A9A" : accent }}>
            {disabled ? "Anteprima" : cta}
          </span>
          <span
            className="h-11 w-11 rounded-full flex items-center justify-center text-white shrink-0"
            style={{
              background: disabled ? "#E5DECF" : accent,
              color: disabled ? "#9A9A9A" : "white",
              boxShadow: disabled ? "none" : `0 10px 22px -6px ${accent}66`,
            }}
          >
            {disabled ? <Lock className="h-4 w-4" /> : (CtaIcon ? <CtaIcon className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />)}
          </span>
        </div>
      </div>
    </motion.button>
  );
}

function HubAurora() {
  return (
    <>
      <div aria-hidden className="fixed inset-0 -z-10"
        style={{ background: "radial-gradient(110% 60% at 50% 0%, rgba(255,176,32,0.22) 0%, rgba(0,0,0,0) 60%)" }} />
      <div aria-hidden className="fixed inset-0 -z-10"
        style={{ background: "radial-gradient(80% 60% at 5% 95%, rgba(45,186,96,0.20) 0%, rgba(0,0,0,0) 60%)" }} />
      <div aria-hidden className="fixed inset-0 -z-10"
        style={{ background: "radial-gradient(80% 60% at 95% 85%, rgba(123,97,255,0.16) 0%, rgba(0,0,0,0) 60%)" }} />
    </>
  );
}
